/* oxlint-disable complexity */
import { useRef, useState } from 'react';
import type React from 'react';

import { Separator } from '@change/ds-core-ui';
import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	SignFormAddressRemovalTracking,
	SignerVelocityTracking,
} from '#src/app/pages/petitionDetails/shared/components';
import {
	useFirstMovement,
	useIsEndorsementsPetitionEnabled,
	usePhoto,
	useStickyBehavior,
	PublicFieldContextProvider,
} from '#src/app/pages/petitionDetails/shared/hooks';
import {
	AfterDescriptionWaypoint,
	AfterMobileActionButtonWaypoint,
} from '#src/app/pages/petitionDetails/shared/waypoints';
import { MembershipAwareness } from '#src/app/shared/components/membershipAwareness';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { PetitionStarter } from '../../components/PetitionStarter';
import { VideoFirstHero } from '../../components/VideoFirstHero';
import { DecisionMakerSection } from '../DecisionMakerSection';
import { DescriptionContainer } from '../description';
import { EmbeddedSignForm } from '../embeddedSignForm';
import { EndorsementsContainer } from '../endorsements';
import { HeadingContainer } from '../Heading';
import { HeadingContainerMobile } from '../HeadingMobile';
import { HeadingMobileWithPhoto } from '../HeadingMobileWithPhoto';
import { HeadingWithPhoto } from '../HeadingWithPhoto';
import { MediaHitsContainer } from '../MediaHitsContainer';
import { MobileActionButtonContainer } from '../mobileActionButton';
import { MomentumBarExperimentContent } from '../momentumBar';
import { MovementContainer } from '../Movement';
import { PetitionTimestamp } from '../PetitionTimestamp';
import { ReportAbuseContainer } from '../ReportAbuse';
import { SapCtaContainer } from '../SapCta';
import { ScrollTrackingWaypointContainer } from '../ScrollTrackingWaypoint';
import { SharePetition } from '../sharePetition/SharePetition';
import { SideContainer } from '../side';
import { SignatureStatsMobileContainer } from '../stats/signatureStats';
import { TagBreadcrumbs } from '../TagBreadcrumbs';
import { TheSupporters } from '../TheSupporters';
import {
	SupporterVoicesFeedProvider,
	useSupporterVoicesFeedContext,
} from '../TheSupporters/components/SupporterVoices/components/SupporterVoicesFeed/shared/context';
import { UpdatesContainer } from '../Updates';

import { useMediaMentionsPlacementExperiment } from './hooks/useMediaMentionsPlacementExperiment';
import { useVideoFirstExperiment } from './hooks/useVideoFirstExperiment';

const VideoFirstMovement: React.FC<{ hasPetitionPhoto: boolean }> = ({ hasPetitionPhoto }) => {
	const firstMovement = useFirstMovement();
	const movementPhoto = usePhoto();

	if (!firstMovement) return null;

	return (
		<div className={hasPetitionPhoto ? 'mb-4 px-4 sm:mb-2 sm:px-0' : 'mb-4 px-4 md:mb-2 md:px-0'}>
			<MovementContainer movement={firstMovement} alwaysExpanded={!movementPhoto} />
		</div>
	);
};

// oxlint-disable-next-line max-lines-per-function
const MainContainerInner: React.FC = () => {
	const {
		data: {
			petition: { id: petitionId, slug, photo },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { showSupporterVoicesFeed },
	} = useSupporterVoicesFeedContext();
	const {
		data: {
			signed,
			experiments: { signFormGdprRadioRemovalExperiment },
		},
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const [videoFirstDesktopRendered, setVideoFirstDesktopRendered] = useState(false);
	const [videoFirstMobileRendered, setVideoFirstMobileRendered] = useState(false);
	const canBeSticky = useStickyBehavior(signatureBlockRef);

	const isEndorsementsEnabled = useIsEndorsementsPetitionEnabled();
	const { experiment: videoFirstExperiment, hasVideoFirstSource, videoFirstSourceUrl } = useVideoFirstExperiment();

	const {
		experiment: mediaMentionsExperiment,
		hasMediaMentions,
		showAboveDecisionMakers: showMediaMentionsAboveDecisionMakers,
	} = useMediaMentionsPlacementExperiment();

	const desktopHeadingClassName = cn('col-span-full hidden w-full md:row-start-1 md:block', {
		'lg:col-start-2 lg:-col-end-1': photo === null,
	});
	const defaultDesktopHeading = (
		<div className={desktopHeadingClassName}>{photo === null ? null : <HeadingWithPhoto />}</div>
	);
	const videoFirstDesktopFallback = (
		<div className={desktopHeadingClassName}>{photo === null ? <HeadingContainer /> : <HeadingWithPhoto />}</div>
	);
	const defaultMobileHeading = photo === null ? <HeadingContainerMobile /> : <HeadingMobileWithPhoto />;
	const videoFirstMobileFallback =
		photo === null ? <HeadingContainerMobile showMovement={false} /> : <HeadingMobileWithPhoto showMovement={false} />;
	const desktopHeading = videoFirstSourceUrl ? (
		<VideoFirstHero
			fallback={videoFirstDesktopFallback}
			onRenderChange={setVideoFirstDesktopRendered}
			sourceUrl={videoFirstSourceUrl}
			viewport="desktop"
		/>
	) : (
		defaultDesktopHeading
	);
	const mobileHeading = videoFirstSourceUrl ? (
		<>
			<VideoFirstHero
				fallback={videoFirstMobileFallback}
				onRenderChange={setVideoFirstMobileRendered}
				sourceUrl={videoFirstSourceUrl}
				viewport="mobile"
			/>
			<VideoFirstMovement hasPetitionPhoto={photo !== null} />
		</>
	) : (
		defaultMobileHeading
	);
	const sidePanelStartsInSecondRow = videoFirstDesktopRendered || (photo === null && videoFirstSourceUrl !== undefined);

	return (
		<PublicFieldContextProvider>
			{/*
			 * Outside both columns because signer velocity exposure must fire in every arm — control and
			 * fake_control render no pill, so this cannot live inside one. Renders nothing and self-gates.
			 */}
			<SignerVelocityTracking />
			{/*
			 * Same reasoning, and additionally outside the sign form: the desktop sidebar, mobile embedded form
			 * and mobile sign modal each mount their own, and exposure dedup is per mount.
			 */}
			<SignFormAddressRemovalTracking />
			<Grid className="grid-rows-[auto] px-0 lg:max-w-[1376px] lg:grid-cols-14! lg:grid-rows-[auto]">
				{desktopHeading}

				<div
					className={cn('col-span-full max-md:px-0 md:col-span-5 md:container lg:col-start-2! lg:col-end-11!', {
						'md:row-start-2': !videoFirstDesktopRendered && photo !== null,
						'md:row-start-3': videoFirstDesktopRendered,
					})}
				>
					{mobileHeading}

					<div className="p-4 md:p-0">
						<div className="hidden md:block">
							<MomentumBarExperimentContent petitionId={petitionId} />
							<DescriptionContainer />

							<ReportAbuseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}
							<PetitionStarter />
						</div>

						<div className="block md:hidden">
							<MobileActionButtonContainer
								layout={videoFirstMobileRendered ? 'sticky-sign' : undefined}
								sticky={false}
								variant="v1"
							/>

							<AfterMobileActionButtonWaypoint />
							<SignatureStatsMobileContainer />

							<MomentumBarExperimentContent petitionId={petitionId} />
							<DescriptionContainer />

							<ReportAbuseContainer />

							{isEndorsementsEnabled && <EndorsementsContainer />}

							<PetitionStarter />
							<EmbeddedSignForm />
						</div>

						<ScrollTrackingWaypointContainer name="scroll_past_body" />
						<AfterDescriptionWaypoint />
					</div>
				</div>
				{/* We need to modify the row-span to match the exact last row before the first full width module */}
				<div
					data-qa="second-column-container"
					className={cn(
						'h-0 md:col-start-6 md:col-end-9 md:row-span-3 md:mb-20 md:h-full lg:col-start-11 lg:col-end-15',
						sidePanelStartsInSecondRow ? 'md:row-start-2' : 'md:row-start-1',
					)}
				>
					<div className="block md:hidden">
						<MobileActionButtonContainer
							layout={videoFirstMobileRendered ? 'sticky-sign' : undefined}
							sticky={true}
							variant="v1"
						/>
					</div>
					<div
						data-qa="sticky-action-panel-container"
						className={cn(
							'relative top-6 right-0 z-12 mt-0 hidden md:mt-6! md:mr-6! md:block md:max-w-[350px] lg:mt-6! lg:mr-6 lg:max-w-[350px]',
							{
								'!sticky': canBeSticky,
							},
						)}
					>
						<div>
							<SideContainer containerRef={signatureBlockRef} />
						</div>
					</div>
				</div>

				{/* Must stay a single instance: dedup is per mount, so a second tracker halves every measured rate. */}
				{hasMediaMentions && <TrackExperimentExposure experiment={mediaMentionsExperiment} resetKeys={[petitionId]} />}
				{hasVideoFirstSource && <TrackExperimentExposure experiment={videoFirstExperiment} resetKeys={[petitionId]} />}

				{/*
				 * Desktop sidebar, mobile embedded, and mobile sign modal all mount their own sign form
				 * concurrently, so this must live here rather than inside the sign form.
				 */}
				<TrackExperimentExposure experiment={signFormGdprRadioRemovalExperiment} resetKeys={[petitionId]} />

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SapCtaContainer />

					{/* Shares this cell rather than being its own Grid child: splitting it adds a row gap for control too. */}
					{showMediaMentionsAboveDecisionMakers && <MediaHitsContainer className="mb-20" />}

					<DecisionMakerSection />
				</div>

				<div
					className={cn('col-span-full', {
						// Mixed feed: full-width breakout for the wide card grid.
						'my-6 px-4 md:px-0': showSupporterVoicesFeed,
						// Legacy comments/videos fallback: centered in the page grid, matching the
						// other petition-body sections (updates, share, etc.).
						'container lg:col-start-2 lg:col-end-11': !showSupporterVoicesFeed,
					})}
				>
					<TheSupporters showSupporterVoicesFeed={showSupporterVoicesFeed} />
				</div>

				<div className="col-span-full my-6 px-4 md:px-0">
					<MembershipAwareness sourceLocation="petition_details" petitionId={petitionId} slug={slug} signed={signed} />
				</div>

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<ScrollTrackingWaypointContainer name="scroll_updates_section" />

					<Separator className="mt-14 mb-8 md:mt-20" />

					<UpdatesContainer />
				</div>

				{!showMediaMentionsAboveDecisionMakers && (
					<div className="col-span-full container lg:col-start-2 lg:col-end-11">
						<MediaHitsContainer className="mt-13 mb-20" />
					</div>
				)}

				<div className="col-span-full container lg:col-start-2 lg:col-end-11">
					<SharePetition />

					<Separator className="mt-14 mb-6 md:mt-20" />
					<PetitionTimestamp />
					<Separator className="my-6" />
					<TagBreadcrumbs />
				</div>
			</Grid>
		</PublicFieldContextProvider>
	);
};

// The feed provider holds the single supporter-voices feed instance so the page layout decision
// (showSupporterVoicesFeed, read in MainContainerInner) and the feed render (SupporterVoicesFeed,
// nested below) share one `useSupporterVideos` state. See CHANGE-94834.
export const MainContainer: React.FC = () => {
	return (
		<SupporterVoicesFeedProvider>
			<MainContainerInner />
		</SupporterVoicesFeedProvider>
	);
};
